<template>
  <form-modal
    :ref="modalName"
    :name="modalName"
    :type="modalType"
    class="BillingAddress"
    title="Billing NEXT"
    submit-name="Save"
    full-height
    @submit="submit"
  >
    <h3 class="mb-4 text-gray-900">
      Company Info <span class="italic text-gray-700">(required)</span>
    </h3>
    <lm-input
      ref="inputName"
      label="Name / Company"
      :maxlength="0"
      @submit="submitFromInput()"
    />
    <lm-input
      ref="inputAddress"
      label="Address"
      :maxlength="0"
      @submit="submitFromInput()"
    />
    <lm-input
      ref="inputCity"
      label="City"
      :maxlength="0"
      @submit="submitFromInput()"
    />
    <lm-input
      ref="inputZip"
      label="Zip / Postcode"
      :maxlength="0"
      @submit="submitFromInput()"
    />
    <lm-input
      ref="inputState"
      label="State / Province"
      :maxlength="0"
      @submit="submitFromInput()"
    />
    <v-select
      v-model="country"
      class="mb-8"
      :class="{error: errorCountry}"
      :options="countryList"
      placeholder="Country"
    />

    <h3 class="mt-12 mb-2 text-gray-900">
      Tax ID <span class="italic text-gray-700">(optional)</span>
    </h3>
    <p class="text-gray-500 text-xs">
      Add your EU VAT or Spain CIF if you need to display it in the invoice.
    </p>
    <div class="mb-6 flex items-center">
      <v-select
        v-model="taxType"
        class="w-72 mt-3 mr-8"
        :options="taxTypeOptions"
        placeholder="Tax Type"
        @change="onTaxTypeChange"
      />
      <lm-input
        ref="inputTax"
        class="w-full mt-px mb0-important"
        :label="resolveTaxLabel"
        :disabled="!taxType"
        :maxlength="0"
        @submit="submitFromInput()"
      />
    </div>
    <h3 class="mt-12 mb-2 text-gray-900">
      Invoice Email Recipient
      <span class="italic text-gray-700">(optional)</span>
    </h3>
    <p class="text-gray-500 text-xs">
      By default, your invoices will be sent to your account's email address
      <b>{{ email }}</b
      >. If you want to use any additional email addresses for receiving
      invoices, you can enter them here:
    </p>
    <lm-email-multiple
      ref="emailMultipleInput"
      class="mt-2"
      @submit="submitFromInput()"
    />
  </form-modal>
</template>

<script>
import {mapGetters} from 'vuex'
import countryList from 'iso-3166-country-list'
import vSelect from 'vue-select'
import {Events, Modal, ModalType} from '@/lib/definitions'
import FormModal from '@/components/modals/FormModal'
import LmInput from '@/components/form/LmInput.vue'
import FormHelper from '@/components/form/formHelper'
import LmEmailMultiple from '@/components/form/LmEmailMultiple.vue'
export default {
  name: 'ModalBillingNext',
  components: {
    FormModal,
    LmInput,
    vSelect,
    LmEmailMultiple
  },
  data() {
    return {
      Events,
      modalName: Modal.BILLING_NEXT,
      modalType: ModalType.SUBMODAL,
      modalRef: null,
      inputs: null,
      country: null,
      taxType: null,
      taxTypeOptions: [
        {label: 'Spain CIF', value: 'es_cif'},
        {label: 'EU VAT', value: 'eu_vat'}
      ],
      countryList: countryList.names,
      errorCountry: false,
      errorTaxType: false
    }
  },
  computed: {
    ...mapGetters(['billing', 'email']),
    resolveTaxLabel() {
      return !this.taxType
        ? ' '
        : this.taxType.value === 'es_cif'
        ? 'Spain CIF (Ex. A12345678)'
        : 'EU VAT (Ex. ESA12345678)'
    }
  },
  watch: {
    country(v) {
      if (v) this.cleanErrors()
    },
    billing() {
      this.init()
    }
  },
  mounted() {
    this.modalRef = this.$refs[this.modalName]
    this.init()
  },
  methods: {
    onTaxTypeChange(tt) {
      if (!tt) {
        this.$refs.inputTax.data = ''
      }
    },
    init() {
      this.inputs = [
        this.$refs.inputName,
        this.$refs.inputAddress,
        this.$refs.inputCity,
        this.$refs.inputState,
        this.$refs.inputZip
      ]
      this.updateInputData()
    },
    updateInputData() {
      if (this.billing) {
        this.$refs.inputName.data = this.billing.name
        this.$refs.inputAddress.data = this.billing.address
        this.$refs.inputCity.data = this.billing.city
        this.$refs.inputZip.data = this.billing.zip
        this.$refs.inputState.data = this.billing.state
        this.$refs.inputTax.data = this.billing.taxId
        this.country =
          this.billing.country && countryList.name(this.billing.country)
        this.taxType = this.taxTypeOptions.find(
          tto => tto.value === this.billing.taxType
        )
        this.$refs.emailMultipleInput.setData(this.billing.emails)
      }
    },
    submitFromInput() {
      this.modalRef.submit()
    },
    validateTaxInfo() {
      this.$refs.inputTax.clearErrors()
      if (!!this.taxType && this.$refs.inputTax.data === '') {
        this.$refs.inputTax.setError('Please fill out or remove Tax Type')
        return false
      }
      return true
    },
    validateCountry() {
      return !!this.country
    },
    submit() {
      this.cleanErrors()
      this.errorCountry = !this.validateCountry()
      this.errorTaxType = !this.validateTaxInfo()
      const billingEmails = this.$refs.emailMultipleInput.getEmails()
      if (
        FormHelper.validate(this.inputs) &&
        !this.errorCountry &&
        !this.errorTaxType &&
        billingEmails
      ) {
        // Call API
        let payload = {
          name: this.$refs.inputName.data,
          address: this.$refs.inputAddress.data,
          city: this.$refs.inputCity.data,
          state: this.$refs.inputState.data,
          zip: this.$refs.inputZip.data,
          country: countryList.code(this.country),
          taxId: this.$refs.inputTax?.data || '',
          taxType: this.taxType?.value,
          billingEmails
        }
        this.$store
          .dispatch('addBillingAddress', payload)
          .then(() => this.modalRef.submitSuccess())
          .catch(error => {
            if (
              error.devCode === 422 &&
              error.devMessage.includes(this.taxType.value)
            ) {
              this.errorTaxType = true
              this.$refs.inputTax.setError(error.devMessage)
            }
            this.modalRef.submitFails(error)
          })
      } else {
        this.modalRef.submitFails()
      }
    },
    cleanErrors() {
      this.errorCountry = false
      this.errorTaxType = false
    }
  }
}
</script>
